import { useT } from '@/i18n/provider';
import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { PLAN_LIST, formatPlanLimit } from '@logpulse/types/plans';

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mb-12 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-800">
        {title}
      </h2>
      <div className="prose-custom">{children}</div>
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="mb-8 scroll-mt-20">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
        {title}
      </h3>
      <div className="prose-custom">{children}</div>
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{children}</p>;
}

function CodeBlock({ children, title }: { children: string; title?: string }) {
  return (
    <div className="my-4 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
      {title && (
        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 text-xs font-medium text-gray-500 dark:text-gray-400">
          {title}
        </div>
      )}
      <pre className="px-4 py-3 bg-gray-950 text-gray-300 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

function Table({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-600 dark:text-gray-400 font-mono text-xs">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ type, children }: { type: 'info' | 'tip' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    tip: 'border-success-500 bg-success-50 dark:bg-success-500/10 text-success-600 dark:text-success-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  const labels = { info: 'Note', tip: 'Tip', warning: 'Warning' };

  return (
    <div className={`my-4 rounded-xl border-l-4 px-4 py-3 ${styles[type]}`}>
      <span className="font-semibold text-sm">{labels[type]}:</span>{' '}
      <span className="text-sm">{children}</span>
    </div>
  );
}

function HttpMethod({ method, path }: { method: string; path: string }) {
  const methodColors: Record<string, string> = {
    GET: 'bg-success-100 dark:bg-success-500/20 text-success-700 dark:text-success-400',
    POST: 'bg-info-100 dark:bg-info-500/20 text-info-700 dark:text-info-400',
  };

  return (
    <div className="flex items-center gap-3 my-3 px-4 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800">
      <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${methodColors[method] || ''}`}>
        {method}
      </span>
      <code className="text-sm text-gray-700 dark:text-gray-300 font-mono">{path}</code>
    </div>
  );
}

const tocItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'endpoints', label: 'Endpoints' },
  { id: 'otlp-logs', label: 'OTLP Logs', level: 1 },
  { id: 'otlp-traces', label: 'OTLP Traces', level: 1 },
  { id: 'authentication', label: 'Authentication' },
  { id: 'logs-schema-mapping', label: 'Logs Schema Mapping' },
  { id: 'severity-mapping', label: 'Severity Mapping', level: 1 },
  { id: 'body-extraction', label: 'Body Extraction', level: 1 },
  { id: 'traces-schema-mapping', label: 'Traces Schema Mapping' },
  { id: 'span-kind-mapping', label: 'Span Kind Values', level: 1 },
  { id: 'span-status-mapping', label: 'Span Status Values', level: 1 },
  { id: 'kubernetes-metadata', label: 'Kubernetes Metadata' },
  { id: 'attribute-types', label: 'Attribute Value Types' },
  { id: 'rate-limits-quotas', label: 'Rate Limits & Quotas' },
  { id: 'error-handling', label: 'Error Handling' },
  { id: 'examples', label: 'Examples' },
  { id: 'otel-collector', label: 'OpenTelemetry Collector', level: 1 },
  { id: 'otel-sdk-logs', label: 'OTel SDK (Logs)', level: 1 },
  { id: 'otel-sdk-traces', label: 'OTel SDK (Traces)', level: 1 },
  { id: 'curl-logs', label: 'cURL (Logs)', level: 1 },
  { id: 'curl-traces', label: 'cURL (Traces)', level: 1 },
];

export function OtlpPage() {
  useDocumentMeta({
    title: 'OTLP Integration: LogPulse Docs',
    description: 'Send OpenTelemetry logs and traces to LogPulse via the standard OTLP/HTTP endpoints.',
  });

  const t = useT();

  return (
    <div className="flex gap-8">
      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Page header */}
        <div className="mb-8">
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 mb-1">
            {t('docs.nav.integrations')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3">
            {t('docs.otlp.title')}
          </h1>
          <P>{t('docs.otlp.intro')}</P>
        </div>

        {/* Overview */}
        <Section id="overview" title={t('docs.otlp.overview.title')}>
          <P>{t('docs.otlp.overview.description')}</P>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 my-6">
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-info-600 dark:text-info-400 mb-1">HTTP</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.otlp.overview.protocolLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-success-600 dark:text-success-400 mb-1">JSON</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.otlp.overview.encodingLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-primary-600 dark:text-primary-400 mb-1">2</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.otlp.overview.signalsLabel')}</div>
            </div>
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 text-center">
              <div className="text-2xl font-bold text-warning-600 dark:text-warning-400 mb-1">10 MB</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">{t('docs.otlp.overview.bodyLimitLabel')}</div>
            </div>
          </div>
          <Callout type="info">{t('docs.otlp.overview.note')}</Callout>
        </Section>

        {/* Endpoints */}
        <Section id="endpoints" title={t('docs.otlp.endpoints.title')}>
          <P>{t('docs.otlp.endpoints.description')}</P>

          <SubSection id="otlp-logs" title={t('docs.otlp.endpoints.logs.title')}>
            <HttpMethod method="POST" path="/v1/logs" />
            <P>{t('docs.otlp.endpoints.logs.description')}</P>
            <Table
              headers={['Parameter', 'Value']}
              rows={[
                ['Full URL', 'https://api.logpulse.io/v1/logs'],
                ['Content-Type', 'application/json'],
                ['Body limit', '10 MB'],
                ['Rate limit', '10,000 requests/min'],
                ['Index', 'main'],
                ['Sourcetype', 'otlp'],
              ]}
            />
          </SubSection>

          <SubSection id="otlp-traces" title={t('docs.otlp.endpoints.traces.title')}>
            <HttpMethod method="POST" path="/v1/traces" />
            <P>{t('docs.otlp.endpoints.traces.description')}</P>
            <Table
              headers={['Parameter', 'Value']}
              rows={[
                ['Full URL', 'https://api.logpulse.io/v1/traces'],
                ['Content-Type', 'application/json'],
                ['Body limit', '10 MB'],
                ['Rate limit', '10,000 requests/min'],
                ['Index', 'traces'],
                ['Sourcetype', 'observability_trace'],
              ]}
            />
          </SubSection>
        </Section>

        {/* Authentication */}
        <Section id="authentication" title={t('docs.otlp.auth.title')}>
          <P>{t('docs.otlp.auth.description')}</P>
          <CodeBlock title="Authorization header">{`Authorization: Bearer YOUR_API_KEY`}</CodeBlock>
          <P>{t('docs.otlp.auth.keyInfo')}</P>
          <Callout type="warning">{t('docs.otlp.auth.warning')}</Callout>
        </Section>

        {/* Logs Schema Mapping */}
        <Section id="logs-schema-mapping" title={t('docs.otlp.logsMapping.title')}>
          <P>{t('docs.otlp.logsMapping.description')}</P>
          <Table
            headers={['OTLP Field', 'LogPulse Field', 'Notes']}
            rows={[
              ['body.stringValue', 'event', t('docs.otlp.logsMapping.body')],
              ['body.kvlistValue', 'event (JSON)', t('docs.otlp.logsMapping.bodyKv')],
              ['severityNumber', 'level', t('docs.otlp.logsMapping.severityNumber')],
              ['severityText', 'level (fallback)', t('docs.otlp.logsMapping.severityText')],
              ['timeUnixNano', 'timestamp', t('docs.otlp.logsMapping.timestamp')],
              ['observedTimeUnixNano', 'timestamp (fallback)', t('docs.otlp.logsMapping.observedTimestamp')],
              ['resource service.name', 'source', t('docs.otlp.logsMapping.serviceName')],
              ['scope.name', 'source (priority)', t('docs.otlp.logsMapping.scopeName')],
              ['resource attributes', 'attributes.*', t('docs.otlp.logsMapping.resourceAttrs')],
              ['log record attributes', 'attributes.*', t('docs.otlp.logsMapping.logAttrs')],
              ['traceId', 'attributes.trace_id', t('docs.otlp.logsMapping.traceId')],
              ['spanId', 'attributes.span_id', t('docs.otlp.logsMapping.spanId')],
            ]}
          />

          <SubSection id="severity-mapping" title={t('docs.otlp.logsMapping.severity.title')}>
            <P>{t('docs.otlp.logsMapping.severity.description')}</P>
            <Table
              headers={['OTLP SeverityNumber', 'LogPulse Level']}
              rows={[
                ['1–4', 'trace'],
                ['5–8', 'debug'],
                ['9–12', 'info'],
                ['13–16', 'warn'],
                ['17–20', 'error'],
                ['21+', 'fatal'],
              ]}
            />
            <Callout type="info">{t('docs.otlp.logsMapping.severity.fallback')}</Callout>
          </SubSection>

          <SubSection id="body-extraction" title={t('docs.otlp.logsMapping.bodyExtraction.title')}>
            <P>{t('docs.otlp.logsMapping.bodyExtraction.description')}</P>
            <CodeBlock title="String body">{`{
  "body": {
    "stringValue": "User login successful from 192.168.1.100"
  }
}`}</CodeBlock>
            <CodeBlock title="Key-value body (converted to JSON)">{`{
  "body": {
    "kvlistValue": {
      "values": [
        { "key": "action", "value": { "stringValue": "login" } },
        { "key": "ip", "value": { "stringValue": "192.168.1.100" } }
      ]
    }
  }
}
// Stored as: {"action":"login","ip":"192.168.1.100"}`}</CodeBlock>
          </SubSection>
        </Section>

        {/* Traces Schema Mapping */}
        <Section id="traces-schema-mapping" title={t('docs.otlp.tracesMapping.title')}>
          <P>{t('docs.otlp.tracesMapping.description')}</P>
          <Table
            headers={['OTLP Span Field', 'LogPulse Attribute', 'Notes']}
            rows={[
              ['traceId', 'trace_id', t('docs.otlp.tracesMapping.traceId')],
              ['spanId', 'span_id', t('docs.otlp.tracesMapping.spanId')],
              ['parentSpanId', 'parent_span_id', t('docs.otlp.tracesMapping.parentSpanId')],
              ['name', 'operation_name', t('docs.otlp.tracesMapping.operationName')],
              ['kind', 'span_kind', t('docs.otlp.tracesMapping.spanKind')],
              ['status.code', 'span_status', t('docs.otlp.tracesMapping.spanStatus')],
              ['status.message', 'span_status_message', t('docs.otlp.tracesMapping.spanStatusMessage')],
              ['startTimeUnixNano', 'timestamp', t('docs.otlp.tracesMapping.startTime')],
              ['endTimeUnixNano - startTimeUnixNano', 'duration_ms', t('docs.otlp.tracesMapping.duration')],
              ['resource service.name', 'service_name', t('docs.otlp.tracesMapping.serviceName')],
              ['http.method / http.request.method', 'http_method', t('docs.otlp.tracesMapping.httpMethod')],
              ['http.url / url.full', 'http_url', t('docs.otlp.tracesMapping.httpUrl')],
              ['http.status_code', 'http_status_code', t('docs.otlp.tracesMapping.httpStatus')],
            ]}
          />
          <P>{t('docs.otlp.tracesMapping.eventNote')}</P>

          <SubSection id="span-kind-mapping" title={t('docs.otlp.tracesMapping.spanKindValues.title')}>
            <Table
              headers={['Numeric Value', 'String Value']}
              rows={[
                ['0', 'unspecified'],
                ['1', 'internal'],
                ['2', 'server'],
                ['3', 'client'],
                ['4', 'producer'],
                ['5', 'consumer'],
              ]}
            />
          </SubSection>

          <SubSection id="span-status-mapping" title={t('docs.otlp.tracesMapping.spanStatusValues.title')}>
            <Table
              headers={['Numeric Value', 'String Value', 'LogPulse Level']}
              rows={[
                ['0', 'unset', 'info'],
                ['1', 'ok', 'info'],
                ['2', 'error', 'error'],
              ]}
            />
            <Callout type="info">{t('docs.otlp.tracesMapping.spanStatusValues.note')}</Callout>
          </SubSection>
        </Section>

        {/* Kubernetes Metadata */}
        <Section id="kubernetes-metadata" title={t('docs.otlp.k8s.title')}>
          <P>{t('docs.otlp.k8s.description')}</P>
          <Table
            headers={['OTLP Resource Attribute', 'LogPulse Field', 'Description']}
            rows={[
              ['k8s.cluster.name', 'cluster', t('docs.otlp.k8s.cluster')],
              ['k8s.namespace.name', 'namespace', t('docs.otlp.k8s.namespace')],
              ['k8s.pod.name', 'pod', t('docs.otlp.k8s.pod')],
              ['k8s.container.name', 'container', t('docs.otlp.k8s.container')],
              ['k8s.node.name', 'node', t('docs.otlp.k8s.node')],
              ['k8s.pod.labels.*', 'labels', t('docs.otlp.k8s.labels')],
              ['k8s.pod.annotation.*', 'annotations', t('docs.otlp.k8s.annotations')],
            ]}
          />
          <Callout type="tip">{t('docs.otlp.k8s.tip')}</Callout>
        </Section>

        {/* Attribute Value Types */}
        <Section id="attribute-types" title={t('docs.otlp.attrTypes.title')}>
          <P>{t('docs.otlp.attrTypes.description')}</P>
          <Table
            headers={['OTLP Type', 'Example', 'LogPulse Storage']}
            rows={[
              ['stringValue', '"hello"', 'Stored as-is'],
              ['intValue', '42 or "42"', 'Converted to string'],
              ['doubleValue', '3.14', 'Converted to string'],
              ['boolValue', 'true', 'Converted to string ("true"/"false")'],
              ['arrayValue', '[1, 2, 3]', 'Not extracted (skipped)'],
              ['kvlistValue', '{key: value}', 'Not extracted (skipped)'],
            ]}
          />
        </Section>

        {/* Rate Limits & Quotas */}
        <Section id="rate-limits-quotas" title={t('docs.otlp.rateLimits.title')}>
          <P>{t('docs.otlp.rateLimits.description')}</P>
          <Table
            headers={['Endpoint', 'Limit', 'Window']}
            rows={[
              ['POST /v1/logs', '10,000', t('docs.otlp.rateLimits.perMinute')],
              ['POST /v1/traces', '10,000', t('docs.otlp.rateLimits.perMinute')],
            ]}
          />

          <SubSection id="quotas" title={t('docs.otlp.rateLimits.quotas.title')}>
            <P>{t('docs.otlp.rateLimits.quotas.description')}</P>
            <Table
              headers={['Plan', 'Daily Limit']}
              rows={PLAN_LIST.map((p) => [p.name, formatPlanLimit(p.id)])}
            />
            <Callout type="warning">{t('docs.otlp.rateLimits.quotas.warning')}</Callout>
          </SubSection>
        </Section>

        {/* Error Handling */}
        <Section id="error-handling" title={t('docs.otlp.errors.title')}>
          <P>{t('docs.otlp.errors.description')}</P>
          <Table
            headers={['Status Code', 'Error Code', 'Meaning', 'Action']}
            rows={[
              ['200', '—', t('docs.otlp.errors.200'), t('docs.otlp.errors.200action')],
              ['400', 'INVALID_OTLP_FORMAT', t('docs.otlp.errors.400'), t('docs.otlp.errors.400action')],
              ['401', 'UNAUTHORIZED', t('docs.otlp.errors.401'), t('docs.otlp.errors.401action')],
              ['401', 'API_KEY_REVOKED', t('docs.otlp.errors.401revoked'), t('docs.otlp.errors.401revokedAction')],
              ['401', 'API_KEY_EXPIRED', t('docs.otlp.errors.401expired'), t('docs.otlp.errors.401expiredAction')],
              ['429', 'QUOTA_EXCEEDED', t('docs.otlp.errors.429'), t('docs.otlp.errors.429action')],
              ['503', 'STORAGE_NOT_CONFIGURED', t('docs.otlp.errors.503'), t('docs.otlp.errors.503action')],
            ]}
          />
          <Callout type="info">{t('docs.otlp.errors.emptyNote')}</Callout>
        </Section>

        {/* Examples */}
        <Section id="examples" title={t('docs.otlp.examples.title')}>
          <P>{t('docs.otlp.examples.description')}</P>

          <SubSection id="otel-collector" title={t('docs.otlp.examples.collector.title')}>
            <P>{t('docs.otlp.examples.collector.description')}</P>
            <CodeBlock title="otel-collector-config.yaml">{`receivers:
  otlp:
    protocols:
      grpc:
        endpoint: 0.0.0.0:4317
      http:
        endpoint: 0.0.0.0:4318

exporters:
  # LogPulse accepts OTLP/HTTP with JSON payloads.
  # The otlphttp exporter appends /v1/logs and /v1/traces automatically
  # to the base endpoint. You can also set logs_endpoint / traces_endpoint
  # explicitly for clarity.
  otlphttp/logpulse-logs:
    endpoint: https://api.logpulse.io
    logs_endpoint: https://api.logpulse.io/v1/logs
    headers:
      Authorization: "Bearer YOUR_API_KEY"
    compression: gzip

  otlphttp/logpulse-traces:
    endpoint: https://api.logpulse.io
    traces_endpoint: https://api.logpulse.io/v1/traces
    headers:
      Authorization: "Bearer YOUR_API_KEY"
    compression: gzip

service:
  pipelines:
    logs:
      receivers: [otlp]
      exporters: [otlphttp/logpulse-logs]
    traces:
      receivers: [otlp]
      exporters: [otlphttp/logpulse-traces]`}</CodeBlock>
            <Callout type="tip">{t('docs.otlp.examples.collector.tip')}</Callout>
          </SubSection>

          <SubSection id="otel-sdk-logs" title={t('docs.otlp.examples.sdkLogs.title')}>
            <P>{t('docs.otlp.examples.sdkLogs.description')}</P>
            <CodeBlock title="Python: OpenTelemetry SDK (Logs)">{`from opentelemetry.sdk._logs import LoggerProvider, LoggingHandler
from opentelemetry.sdk._logs.export import BatchLogRecordProcessor
from opentelemetry.exporter.otlp.proto.http._log_exporter import OTLPLogExporter
import logging

# Configure the OTLP exporter
exporter = OTLPLogExporter(
    endpoint="https://api.logpulse.io/v1/logs",
    headers={"Authorization": "Bearer YOUR_API_KEY"},
)

# Set up the logger provider
logger_provider = LoggerProvider()
logger_provider.add_log_record_processor(
    BatchLogRecordProcessor(exporter)
)

# Attach to Python logging
handler = LoggingHandler(
    level=logging.DEBUG,
    logger_provider=logger_provider,
)
logging.getLogger().addHandler(handler)

# Use standard Python logging
logger = logging.getLogger("my-service")
logger.info("User signed up", extra={"user_id": "usr_123"})`}</CodeBlock>
          </SubSection>

          <SubSection id="otel-sdk-traces" title={t('docs.otlp.examples.sdkTraces.title')}>
            <P>{t('docs.otlp.examples.sdkTraces.description')}</P>
            <CodeBlock title="Node.js: OpenTelemetry SDK (Traces)">{`const { NodeSDK } = require('@opentelemetry/sdk-node');
const { OTLPTraceExporter } = require('@opentelemetry/exporter-trace-otlp-http');
const { getNodeAutoInstrumentations } = require('@opentelemetry/auto-instrumentations-node');

const sdk = new NodeSDK({
  traceExporter: new OTLPTraceExporter({
    url: 'https://api.logpulse.io/v1/traces',
    headers: {
      'Authorization': 'Bearer YOUR_API_KEY',
    },
  }),
  instrumentations: [getNodeAutoInstrumentations()],
  serviceName: 'my-node-service',
});

sdk.start();

// Your application code, spans are automatically created
// for HTTP requests, database calls, etc.`}</CodeBlock>
          </SubSection>

          <SubSection id="curl-logs" title={t('docs.otlp.examples.curlLogs.title')}>
            <CodeBlock title="cURL: Send OTLP logs">{`curl -X POST https://api.logpulse.io/v1/logs \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "resourceLogs": [{
      "resource": {
        "attributes": [
          { "key": "service.name", "value": { "stringValue": "my-service" } },
          { "key": "k8s.namespace.name", "value": { "stringValue": "production" } }
        ]
      },
      "scopeLogs": [{
        "scope": { "name": "my-library", "version": "1.0.0" },
        "logRecords": [{
          "timeUnixNano": "1711018800000000000",
          "severityNumber": 9,
          "severityText": "INFO",
          "body": { "stringValue": "Order processed successfully" },
          "attributes": [
            { "key": "order_id", "value": { "stringValue": "ord_abc123" } },
            { "key": "amount", "value": { "doubleValue": 49.99 } }
          ]
        }]
      }]
    }]
  }'`}</CodeBlock>
          </SubSection>

          <SubSection id="curl-traces" title={t('docs.otlp.examples.curlTraces.title')}>
            <CodeBlock title="cURL: Send OTLP traces">{`curl -X POST https://api.logpulse.io/v1/traces \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -d '{
    "resourceSpans": [{
      "resource": {
        "attributes": [
          { "key": "service.name", "value": { "stringValue": "api-gateway" } }
        ]
      },
      "scopeSpans": [{
        "scope": { "name": "express" },
        "spans": [{
          "traceId": "5b8efff798038103d269b633813fc60c",
          "spanId": "eee19b7ec3c1b174",
          "name": "GET /api/users",
          "kind": 2,
          "startTimeUnixNano": "1711018800000000000",
          "endTimeUnixNano": "1711018800045000000",
          "attributes": [
            { "key": "http.method", "value": { "stringValue": "GET" } },
            { "key": "http.url", "value": { "stringValue": "/api/users" } },
            { "key": "http.status_code", "value": { "intValue": 200 } }
          ],
          "status": { "code": 1 }
        }]
      }]
    }]
  }'`}</CodeBlock>
          </SubSection>
        </Section>
      </div>

      <TableOfContents items={tocItems} />
    </div>
  );
}
